import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, MapPin, X } from "lucide-react";
import { api } from "../lib/api";
import { useAppSettings } from "../lib/useAppSettings";
import MaterialPhotos from "./MaterialPhotos";
import DestinationPicker, { destBody, emptyDest, resolveDest, useDestinations } from "./DestinationPicker";

const errText = (e, f) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || f; };

/**
 * FLD-13 — one move sheet for the whole Install view: one entry or many, a quantity per entry,
 * the shared destination picker, a full preview, one Confirm Move. Nothing becomes installed.
 */
export default function MoveMaterialDialog({ projectId, actor, entries = [], viewFilter, mode = "own_room",
                                             title = "Move material", onClose, onDone }) {
  const [rows, setRows] = useState(() => entries.map((e) => ({ ...e, qty: e.qty ?? e.available })));
  const [dest, setDest] = useState({ ...emptyDest, mode });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // FT-2 §6 — a move can document where the material landed. OFF until Settings > Field says so.
  const photosOn = useAppSettings().material_placement_photos === true;
  const [photoAt, setPhotoAt] = useState("");
  const data = useDestinations(projectId, rows.map((r) => r.placement_id), viewFilter);

  const resolved = useMemo(() => resolveDest(dest, data, rows), [dest, data, rows]);
  const moving = resolved.filter((r) => !r.noop && !r.flagged);
  const units = moving.reduce((s, r) => s + Number(r.qty || 0), 0);
  const bad = rows.some((r) => !(Number(r.qty) > 0) || Number(r.qty) > r.available + 1e-9);
  const byDest = useMemo(() => {
    const m = new Map();
    moving.forEach((r) => m.set(r.to_label, (m.get(r.to_label) || 0) + Number(r.qty || 0)));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [moving]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/material/move", {
        project_id: projectId, by: actor, note, confirm: true, dest: destBody(dest, data),
        entries: resolved.filter((r) => !r.flagged).map((r) => ({ placement_id: r.placement_id, qty: Number(r.qty), room_key: r.to_room_key || "" })),
      });
      toast.success(`${res.units} unit(s) moved`, { description: res.message });
      await onDone?.();
      if (photosOn && byDest.length === 1) { setPhotoAt(byDest[0][0]); return; }
      onClose?.();
    } catch (e) { toast.error(errText(e, "Nothing was moved — the move was refused")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[140] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="move-material-dialog">
      {photoAt && (
        <MaterialPhotos projectId={projectId} locationLabel={photoAt} actor={actor}
                        onClose={() => { setPhotoAt(""); onClose?.(); }}
                        onChanged={() => onDone?.()} />
      )}
      <div className="bg-white w-full sm:max-w-2xl max-h-[94vh] rounded-t-lg sm:rounded-lg flex flex-col">
        <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-gray-200">
          <MapPin className="w-4 h-4 text-blue-700" /><b className="text-[15px] sm:text-sm">{title}</b>
          <button onClick={onClose} aria-label="Close" className="ml-auto min-h-[44px] min-w-[44px] inline-flex items-center justify-center" data-testid="move-material-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2 space-y-2">
          <div className="divide-y divide-gray-100" data-testid="move-material-entries">
            {rows.map((r) => (
              <div key={r.placement_id} className="py-1.5 flex flex-wrap items-center gap-2 text-[13px] sm:text-[12px]" data-testid={`move-entry-${r.placement_id}`}>
                <span className="flex-1 min-w-[150px]">
                  <b className="block leading-snug">{r.description}</b>
                  <span className="text-gray-500">{r.uom || "Each"} · at {r.from_location || "—"} · available {r.available}</span>
                </span>
                <Input type="number" min="0" max={r.available} step="any" value={r.qty} className="h-11 sm:h-9 w-20 text-right"
                       data-testid={`move-entry-qty-${r.placement_id}`}
                       onChange={(e) => setRows((c) => c.map((x) => x.placement_id === r.placement_id
                         ? { ...x, qty: Math.min(Number(e.target.value), r.available) } : x))} />
                <button type="button" onClick={() => setRows((c) => c.filter((x) => x.placement_id !== r.placement_id))}
                        className="min-h-[44px] sm:min-h-[32px] px-2 text-[11px] underline text-gray-500" data-testid={`move-entry-remove-${r.placement_id}`}>Remove</button>
              </div>
            ))}
            {rows.length === 0 && <div className="py-4 text-center text-sm text-gray-500" data-testid="move-material-empty">Nothing selected.</div>}
          </div>

          <div className="text-[11px] uppercase tracking-wide text-gray-500">Where did it go?</div>
          <DestinationPicker data={data} entries={rows} value={dest} onChange={setDest} testId="move-material" />

          <div className="rounded border border-gray-200 bg-slate-50 p-2 text-[12px] space-y-0.5" data-testid="move-material-preview">
            <div><b data-testid="move-material-units">{units}</b> unit(s) · {moving.length} entry(ies) → <b>{byDest.length}</b> destination(s)</div>
            {byDest.map(([label, q]) => <div key={label} data-testid={`move-preview-dest-${label.replace(/[^\w]+/g, "_")}`}>{label}: <b className="tabular-nums">{q}</b></div>)}
            {resolved.some((r) => r.noop) && <div className="text-gray-600" data-testid="move-preview-noop">{resolved.filter((r) => r.noop).length} already in that location — skipped, no history written.</div>}
            {resolved.some((r) => r.flagged) && <div className="text-amber-900" data-testid="move-preview-flagged">{resolved.filter((r) => r.flagged).length} entry(ies) still need a destination and are not included.</div>}
            {moving.some((r) => r.staging) && <div className="text-amber-900">Staging location — not spread into assigned rooms.</div>}
            <div className="text-gray-600">Stays on site and <b>Not Installed</b>. FRS and installed quantities are untouched.</div>
          </div>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="h-10" data-testid="move-material-note" />
        </div>

        <div className="shrink-0 border-t border-gray-200 px-3 py-2 flex flex-wrap gap-1 items-center">
          {!data && <Loader2 className="w-4 h-4 animate-spin" />}
          <Button className="min-h-[48px] sm:min-h-[40px] flex-1 sm:flex-none" disabled={busy || bad || units <= 0} onClick={confirm} data-testid="move-material-confirm">
            Confirm Move — {units} unit(s)
          </Button>
          <Button variant="ghost" className="min-h-[48px] sm:min-h-[40px]" onClick={onClose} data-testid="move-material-cancel">Cancel</Button>
        </div>
      </div>
    </div>
  );
}
